import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { useLocalStorage } from './useLocalStorage'

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

export enum Theme {
  auto = 'auto',
  dark = 'dark',
  light = 'light',
}

export const useTheme = (): {
  onThemeChange: (value: Theme) => void
  themeLocalStorageValue: Theme
  currentTheme: Exclude<Theme, Theme.auto>
} => {
  const [themeLocalStorageValue, setThemeLocalStorageValue] =
    useLocalStorage<Theme>('Theme', '', Theme.auto)

  const onThemeChange = useCallback(
    (value: Theme) => {
      setThemeLocalStorageValue(value)
    },
    [setThemeLocalStorageValue]
  )

  const [globalTheme, setGlobalTheme] = useState<Exclude<Theme, Theme.auto>>(
    typeof window !== 'undefined' && window.matchMedia(DARK_MEDIA_QUERY).matches
      ? Theme.dark
      : Theme.light
  )

  const handleMediaQueryChange = (e) => {
    setGlobalTheme(e.target.matches ? Theme.dark : Theme.light)
  }

  const isBrowser =
    typeof window !== 'undefined' && typeof navigator !== 'undefined'
  const useSSRLayoutEffect = isBrowser ? useLayoutEffect : useEffect

  useSSRLayoutEffect(() => {
    if (isBrowser) {
      window
        .matchMedia(DARK_MEDIA_QUERY)
        .addEventListener('change', handleMediaQueryChange)

      // This is custom event listener for testing dark theme in cypress.
      window.Cypress &&
        window.addEventListener(
          'force_theme_cypress',
          (e: any) => e?.detail && setThemeLocalStorageValue(e.detail)
        )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentTheme =
    themeLocalStorageValue === 'auto' ? globalTheme : themeLocalStorageValue

  return {
    onThemeChange,
    themeLocalStorageValue,
    currentTheme,
  }
}
